import type RTKClient from '@cloudflare/realtimekit';

export interface MicLevel {
  /** 0..1, roughly linear in perceived loudness (dBFS mapped from -60..0). */
  level: number;
  /** The mic is on but has produced no meaningful signal for a while. */
  silent: boolean;
}

// Below this we consider the input "no signal" (Discord's voice SDK uses the
// same -60 dBFS default). A broken/OS-muted/wrong device reads well under it.
const SILENCE_DBFS = -60;
// Sustained silence before we say so — brief pauses in speech are normal.
const SILENCE_HOLD_MS = 3000;
const SAMPLE_MS = 100;

/**
 * Watch the local mic's raw input level. Taps `meeting.self.audioTrack` via an
 * AnalyserNode (a second consumer of the same track — capture is unaffected)
 * and follows the track across `audioUpdate` (mute/unmute, device change).
 * Reports `silent` only while the mic is enabled. Returns a stop function.
 */
export function watchMicLevel(
  meeting: RTKClient,
  onLevel: (l: MicLevel) => void,
): () => void {
  let ctx: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let timer: number | null = null;
  let buf: Float32Array<ArrayBuffer> | null = null;
  let silentSince: number | null = null;
  let currentTrack: MediaStreamTrack | null = null;

  const teardown = () => {
    if (timer !== null) window.clearInterval(timer);
    timer = null;
    source?.disconnect();
    analyser?.disconnect();
    source = null;
    analyser = null;
    currentTrack = null;
    silentSince = null;
  };

  const attach = (track: MediaStreamTrack | null | undefined, enabled: boolean) => {
    if (!enabled || !track || track.readyState !== 'live') {
      teardown();
      onLevel({ level: 0, silent: false });
      return;
    }
    if (track === currentTrack) return;
    teardown();
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    buf = new Float32Array(analyser.fftSize);
    source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(analyser);
    currentTrack = track;

    timer = window.setInterval(() => {
      if (!analyser || !buf) return;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      const dbfs = rms > 0 ? 20 * Math.log10(rms) : -Infinity;
      const level = Math.min(1, Math.max(0, (dbfs - SILENCE_DBFS) / -SILENCE_DBFS));
      const now = Date.now();
      if (dbfs < SILENCE_DBFS) silentSince ??= now;
      else silentSince = null;
      onLevel({ level, silent: silentSince !== null && now - silentSince >= SILENCE_HOLD_MS });
    }, SAMPLE_MS);
  };

  const onAudioUpdate = ({
    audioEnabled,
    audioTrack,
  }: {
    audioEnabled: boolean;
    audioTrack: MediaStreamTrack;
  }) => attach(audioTrack, audioEnabled);

  meeting.self.on('audioUpdate', onAudioUpdate);
  attach(meeting.self.audioTrack, meeting.self.audioEnabled);

  return () => {
    meeting.self.off('audioUpdate', onAudioUpdate);
    teardown();
    void ctx?.close();
    ctx = null;
  };
}
